import { ChevronLeft, ChevronRight, Download, Moon, Sun, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { ImageViewer } from './viewers/ImageViewer';
import { PdfViewer } from './viewers/PdfViewer';
import { FontViewer } from './viewers/FontViewer';
import { TextViewer } from './viewers/TextViewer';
import { FileFallback, getFileTypeMeta } from './viewers/FileFallback';
import { VideoViewer } from './viewers/VideoViewer';
import { useThemeStore } from '../../stores/theme';

export interface FilePreviewItem {
	id: string;
	url: string;
	name: string;
	mimeType: string;
	size?: number | null;
	/** Server-rendered raster preview, for formats the browser can't display
	 *  itself (PDF, .ai, EPS). Null when it isn't ready or isn't possible. */
	thumbnailUrl?: string | null;
}

type FileType = 'image' | 'video' | 'pdf' | 'font' | 'text' | 'unknown';

const TEXT_EXTENSIONS = [
	'txt',
	'csv',
	'tsv',
	'log',
	'md',
	'markdown',
	'json',
	'xml',
	'yaml',
	'yml',
	'html',
	'css',
	'js',
	'ts',
	'ini',
	'conf',
];

const TEXT_MIME_TYPES = [
	'application/json',
	'application/xml',
	'application/csv',
	'application/x-yaml',
];

export function detectFileType(mimeType: string, name: string): FileType {
	if (mimeType.startsWith('image/')) return 'image';
	if (mimeType.startsWith('video/')) return 'video';
	if (mimeType === 'application/pdf') return 'pdf';
	if (
		mimeType.startsWith('font/') ||
		mimeType === 'application/x-font-ttf' ||
		mimeType === 'application/x-font-otf' ||
		mimeType === 'application/x-font-woff' ||
		mimeType === 'application/font-woff' ||
		mimeType === 'application/font-woff2' ||
		mimeType === 'application/vnd.ms-fontobject'
	)
		return 'font';

	const ext = name.split('.').pop()?.toLowerCase();
	if (ext && ['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) return 'font';
	if (ext === 'pdf') return 'pdf';
	if (ext && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'bmp'].includes(ext))
		return 'image';
	if (ext && ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'].includes(ext)) return 'video';

	if (
		mimeType.startsWith('text/') ||
		TEXT_MIME_TYPES.includes(mimeType) ||
		(ext && TEXT_EXTENSIONS.includes(ext))
	)
		return 'text';

	return 'unknown';
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileTypeLabel(type: FileType, mimeType: string, name: string): string {
	if (type === 'pdf') return 'PDF';
	if (type === 'video') return 'Video';
	if (type === 'font') return 'Font';
	if (type === 'image') {
		return mimeType.split('/')[1]?.toUpperCase().replace('SVG+XML', 'SVG') ?? 'Image';
	}
	return getFileTypeMeta(name, mimeType).label;
}

/** Short badge for the thumbnail strip (kept to a few chars). */
function thumbBadge(type: FileType, mimeType: string, name: string): string {
	if (type === 'pdf') return 'PDF';
	if (type === 'font') return 'FONT';
	const ext = name.split('.').pop()?.toLowerCase();
	if (ext && ext.length <= 4 && ext !== name.toLowerCase()) return ext;
	return type === 'text' ? 'TXT' : 'FILE';
}

interface FilePreviewProps {
	files: FilePreviewItem[];
	initialIndex?: number;
	onClose: () => void;
}

export function FilePreview({ files, initialIndex = 0, onClose }: FilePreviewProps) {
	const [index, setIndex] = useState(initialIndex);
	// Seed the artwork backdrop from the app theme — dark theme opens on the
	// dark field, light theme on the light one. Read once at mount rather
	// than subscribed, so flipping the app theme mid-preview doesn't yank a
	// backdrop the user may have chosen deliberately.
	const [lightBg, setLightBg] = useState(() => useThemeStore.getState().theme === 'light');
	const [pdfMeta, setPdfMeta] = useState<{ numPages: number } | null>(null);
	const file = files[index];
	const type = file ? detectFileType(file.mimeType, file.name) : 'unknown';
	const hasPrev = index > 0;
	const hasNext = index < files.length - 1;
	const showBgToggle = type === 'image' || type === 'font';

	const goTo = useCallback(
		(idx: number) => {
			if (idx >= 0 && idx < files.length) {
				setIndex(idx);
				setPdfMeta(null);
			}
		},
		[files.length]
	);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
			if (e.key === 'ArrowLeft' && hasPrev) goTo(index - 1);
			if (e.key === 'ArrowRight' && hasNext) goTo(index + 1);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [index, hasPrev, hasNext, onClose, goTo]);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	const handleDownload = async () => {
		if (!file) return;
		try {
			const response = await fetch(file.url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = file.name;
			a.click();
			URL.revokeObjectURL(blobUrl);
		} catch {
			window.open(file.url, '_blank');
		}
	};

	if (!file) return null;

	return createPortal(
		<div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
			<div className="absolute inset-0 bg-black/80" />
			<div
				className="relative mx-4 flex h-[85vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl border border-line-1 bg-surface-sunken"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className="flex shrink-0 items-center justify-between border-b border-line-1 px-4 py-3">
					<div className="min-w-0 flex-1">
						<div className="truncate text-base font-medium text-text-1">
							{file.name}
						</div>
						<div className="flex items-center gap-2 text-xs text-text-4">
							<span>{fileTypeLabel(type, file.mimeType, file.name)}</span>
							{pdfMeta && (
								<>
									<span>·</span>
									<span>{pdfMeta.numPages} pages</span>
								</>
							)}
							{files.length > 1 && (
								<>
									<span>·</span>
									<span>
										{index + 1} of {files.length}
									</span>
								</>
							)}
							{file.size != null && file.size > 0 && (
								<>
									<span>·</span>
									<span>{formatFileSize(file.size)}</span>
								</>
							)}
						</div>
					</div>
					<div className="flex items-center gap-1">
						{showBgToggle && (
							<button
								type="button"
								onClick={() => setLightBg(!lightBg)}
								className="flex h-7 w-7 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								title={lightBg ? 'Dark background' : 'Light background'}
							>
								{lightBg ? <Moon size={14} /> : <Sun size={14} />}
							</button>
						)}
						<button
							type="button"
							onClick={handleDownload}
							className="flex h-7 w-7 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<Download size={14} />
						</button>
						<button
							type="button"
							onClick={onClose}
							className="flex h-7 w-7 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<X size={14} />
						</button>
					</div>
				</div>

				{/* Content */}
				<div className="relative min-h-0 flex-1">
					{type === 'image' && (
						<ImageViewer url={file.url} name={file.name} lightBg={lightBg} />
					)}
					{type === 'video' && <VideoViewer url={file.url} name={file.name} />}
					{type === 'pdf' && <PdfViewer url={file.url} onMetadata={setPdfMeta} />}
					{type === 'font' && (
						<FontViewer url={file.url} name={file.name} lightBg={lightBg} />
					)}
					{type === 'text' && (
						<TextViewer url={file.url} name={file.name} mimeType={file.mimeType} />
					)}
					{type === 'unknown' && file.thumbnailUrl && (
						<ImageViewer url={file.thumbnailUrl} name={file.name} lightBg={lightBg} />
					)}
					{type === 'unknown' && !file.thumbnailUrl && (
						<FileFallback
							name={file.name}
							mimeType={file.mimeType}
							sizeLabel={
								file.size != null && file.size > 0
									? formatFileSize(file.size)
									: null
							}
						/>
					)}

					{/* Navigation arrows — overlaid on content */}
					{hasPrev && (
						<button
							type="button"
							onClick={() => goTo(index - 1)}
							className="absolute left-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-line-1 bg-surface text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<ChevronLeft size={16} />
						</button>
					)}
					{hasNext && (
						<button
							type="button"
							onClick={() => goTo(index + 1)}
							className="absolute right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-line-1 bg-surface text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<ChevronRight size={16} />
						</button>
					)}
				</div>

				{/* Thumbnail strip */}
				{files.length > 1 && (
					<div className="flex shrink-0 items-center justify-center gap-1.5 border-t border-line-1 px-4 py-3">
						{files.map((thumb, i) => {
							const thumbType = detectFileType(thumb.mimeType, thumb.name);
							const thumbSrc =
								thumb.thumbnailUrl ?? (thumbType === 'image' ? thumb.url : null);
							return (
								<button
									key={thumb.id}
									type="button"
									onClick={() => goTo(i)}
									className={cn(
										'bg-stripes flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border transition-all',
										i === index
											? 'border-accent ring-1 ring-accent/30'
											: 'border-line-1 opacity-40 hover:opacity-70'
									)}
								>
									{thumbSrc ? (
										<img
											src={thumbSrc}
											alt={thumb.name}
											className="h-full w-full object-contain p-1"
										/>
									) : (
										<span className="text-2xs font-medium uppercase text-text-3">
											{thumbBadge(thumbType, thumb.mimeType, thumb.name)}
										</span>
									)}
								</button>
							);
						})}
					</div>
				)}
			</div>
		</div>,
		document.body
	);
}
